import React, { useState } from 'react';
import { InstagramAuditResult, AuditScoreCategory } from '../../types/socialMediaAudit';
import { generateSocialMediaAuditPDF } from '../../services/socialMediaAudit/pdfSocialMediaAuditService';
import { RadarChart } from '../audit/RadarChart';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { AWIN_AFFILIATE_URL_PREFIX, NAMECHEAP_SEARCH_URL_PREFIX } from '../../constants';

interface Props {
  result: InstagramAuditResult;
}

const PillarCard: React.FC<{ category: AuditScoreCategory }> = ({ category }) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const badgeColors =
    category.riskLevel === 'Low'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : category.riskLevel === 'Medium'
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-rose-100 text-rose-800 border-rose-200';

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col justify-between">
      <div>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-slate-100">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">
              Weight: {category.weight}x
            </span>
            <h3 className="text-lg font-bold text-slate-900">{category.categoryName}</h3>
          </div>
          <div className={`px-3 py-1 rounded-lg border text-xs font-bold shrink-0 ${badgeColors}`}>
            Grade {category.grade} • {category.score}/100
          </div>
        </div>

        {/* What was evaluated & Why it matters */}
        <div className="grid grid-cols-1 gap-2.5 mb-4 bg-slate-50 p-3.5 rounded-xl border border-slate-100 text-xs">
          <div>
            <span className="font-bold text-slate-700 uppercase tracking-wider block mb-0.5">
              What Was Evaluated:
            </span>
            <p className="text-slate-600 leading-relaxed">{category.whatWasEvaluated}</p>
          </div>
          <div>
            <span className="font-bold text-indigo-700 uppercase tracking-wider block mb-0.5">
              Why It Matters:
            </span>
            <p className="text-slate-600 leading-relaxed">{category.whyItMatters}</p>
          </div>
        </div>

        {/* Diagnostic Findings */}
        <div className="mb-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            Diagnostic Findings
          </h4>
          <ul className="space-y-1.5">
            {category.findings.map((finding, idx) => (
              <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                <span className="text-indigo-600 font-bold mt-0.5">•</span>
                <span>{finding}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actionable Recommendations */}
        {category.recommendations.length > 0 && (
          <div className="mb-4 bg-indigo-50/60 border border-indigo-100 rounded-xl p-3.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-800 mb-2">
              Consultant Recommendations
            </h4>
            <ul className="space-y-1.5">
              {category.recommendations.map((rec, idx) => (
                <li key={idx} className="text-xs text-indigo-950 flex items-start gap-2 leading-relaxed">
                  <span className="font-bold text-indigo-600">→</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Expandable Criterion-by-Criterion Mathematical Breakdown */}
      <div className="pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
          <span className="italic">{category.benchmark}</span>
          <button
            type="button"
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="text-indigo-600 font-semibold hover:underline shrink-0 ml-2"
          >
            {showBreakdown ? 'Hide Score Math ▲' : 'View Score Math ▼'}
          </button>
        </div>

        {showBreakdown && (
          <div className="mt-3 space-y-2 bg-slate-50 p-3 rounded-lg border border-slate-200/80">
            {category.scoreBreakdown.map((item, i) => (
              <div key={i} className="text-xs border-b border-slate-200/60 last:border-none pb-2 last:pb-0">
                <div className="flex justify-between font-semibold text-slate-800">
                  <span>{item.criterion}</span>
                  <span
                    className={
                      item.status === 'optimal'
                        ? 'text-emerald-700'
                        : item.status === 'moderate'
                        ? 'text-amber-700'
                        : 'text-rose-700'
                    }
                  >
                    {item.pointsEarned} / {item.maxPoints} pts
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">{item.rationale}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const InstagramAuditResults: React.FC<Props> = ({ result }) => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [companyName, setCompanyName] = useState('BrandGenius Strategy Group');
  const [customNotes, setCustomNotes] = useState('');
  const [uploadedLogoBase64, setUploadedLogoBase64] = useState<string | undefined>(
    user?.customLogoBase64
  );
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setUploadedLogoBase64(reader.result);
        showToast('Custom white-label logo loaded for PDF report.');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const effectiveLogo = uploadedLogoBase64 || user?.customLogoBase64;
      const doc = await generateSocialMediaAuditPDF(
        result,
        companyName,
        effectiveLogo,
        customNotes
      );
      doc.save(`Instagram_Brand_Audit_${result.handle}_${new Date().toISOString().split('T')[0]}.pdf`);
      showToast('White-label Social Media Audit PDF downloaded!');
    } catch (error) {
      console.error('Failed to generate PDF', error);
      showToast('Could not generate PDF report. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const cleanDomainQuery = result.handle.replace(/[^a-zA-Z0-9]/g, '');
  const encodedDestination = encodeURIComponent(
    `${NAMECHEAP_SEARCH_URL_PREFIX}${encodeURIComponent(cleanDomainQuery)}`
  );
  const affiliateDomainCheckUrl = `${AWIN_AFFILIATE_URL_PREFIX}${encodedDestination}`;

  const radarData = result.categories.map((cat) => ({
    label: cat.shortLabel,
    value: cat.score,
  }));

  return (
    <div className="space-y-8">
      {/* Executive Header & Radar Chart */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <span className="px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-bold uppercase tracking-wider">
                {result.dataCompleteness.mode} ({result.dataCompleteness.percentage}% Data Coverage)
              </span>
              <span className="text-xs text-slate-400">
                Audited {new Date(result.auditedAt).toLocaleDateString()}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              @{result.handle} — {result.brandName}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Industry: <strong className="text-slate-700">{result.industry}</strong> • Brand Archetype:{' '}
              <strong className="text-indigo-600">{result.brandArchetype}</strong>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={affiliateDomainCheckUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm transition-colors"
            >
              <span>Check {cleanDomainQuery}.com Domain</span>
              <span>↗</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-6 items-center">
          {/* Score & Executive Summary */}
          <div className="lg:col-span-7 space-y-5">
            <div className="flex items-center gap-6">
              <div className="w-28 h-28 rounded-2xl bg-indigo-600 text-white flex flex-col items-center justify-center shadow-lg shadow-indigo-600/20 shrink-0">
                <span className="text-4xl font-extrabold leading-none">{result.overallScore}</span>
                <span className="text-xs uppercase tracking-widest opacity-80 mt-1">/ 100</span>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-slate-100 text-slate-800 text-xs font-bold mb-2">
                  <span>Grade: {result.grade}</span>
                  <span>•</span>
                  <span>{result.riskLevel} Strategic Risk</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Executive Diagnostic Summary</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-1">
                  {result.executiveSummary}
                </p>
              </div>
            </div>

            {/* 4-Metric Snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                <span className="text-[11px] font-semibold uppercase text-slate-400 block">
                  Engagement Rate
                </span>
                <span className="text-xl font-extrabold text-slate-900">
                  {result.keyMetricsSnapshot.engagementRatePct}%
                </span>
                <span className="text-[11px] text-indigo-600 font-medium block truncate">
                  {result.keyMetricsSnapshot.engagementTierLabel}
                </span>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                <span className="text-[11px] font-semibold uppercase text-slate-400 block">
                  Authority Ratio
                </span>
                <span className="text-xl font-extrabold text-slate-900">
                  {result.keyMetricsSnapshot.followerToFollowingRatio}:1
                </span>
                <span className="text-[11px] text-slate-500 block">Followers : Following</span>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                <span className="text-[11px] font-semibold uppercase text-slate-400 block">
                  Posting Velocity
                </span>
                <span className="text-xl font-extrabold text-slate-900">
                  {result.keyMetricsSnapshot.postingCadencePerWeek}/wk
                </span>
                <span className="text-[11px] text-slate-500 block">Target: 4–6/wk</span>
              </div>
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                <span className="text-[11px] font-semibold uppercase text-slate-400 block">
                  Bio Funnel Score
                </span>
                <span className="text-xl font-extrabold text-slate-900">
                  {result.keyMetricsSnapshot.bioConversionScore}/100
                </span>
                <span className="text-[11px] text-slate-500 block">Conversion Readiness</span>
              </div>
            </div>
          </div>

          {/* 6-Axis Radar Chart */}
          <div className="lg:col-span-5 flex flex-col items-center justify-center bg-slate-50 rounded-2xl p-4 border border-slate-100">
            <RadarChart data={radarData} size={290} color="#4F46E5" />
            <span className="text-xs font-semibold text-slate-500 mt-1">
              6-Pillar Brand Presence Topology
            </span>
          </div>
        </div>
      </div>

      {/* Priority Consultant Action Matrix */}
      {result.priorityActions.length > 0 && (
        <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 shadow-sm">
          <div className="mb-5">
            <span className="text-xs font-bold uppercase tracking-widest text-indigo-400">
              High-Impact Optimization Roadmap
            </span>
            <h3 className="text-xl font-bold mt-1">
              Top Priority Actions Ordered by Expected ROI
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.priorityActions.map((item, idx) => (
              <div
                key={idx}
                className="p-4 rounded-xl bg-white/5 border border-white/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span
                      className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${
                        item.priority === 'Critical'
                          ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                          : item.priority === 'High'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                          : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                      }`}
                    >
                      {item.priority} Priority
                    </span>
                    <span className="text-xs text-slate-400">{item.category}</span>
                  </div>
                  <p className="text-sm font-medium text-white leading-relaxed">{item.action}</p>
                </div>
                <p className="text-xs text-emerald-400 font-medium mt-3 pt-2 border-t border-white/10">
                  Expected Impact: {item.expectedImpact}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6 Pillar Deep-Dive Cards */}
      <div>
        <div className="mb-5">
          <h3 className="text-2xl font-bold text-slate-900">
            6-Pillar Deterministic Breakdown
          </h3>
          <p className="text-sm text-slate-500">
            Every pillar is scored 0–100 using transparent, reproducible rules. Click "View Score Math" on any card to inspect exact criterion weights.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {result.categories.map((cat) => (
            <PillarCard key={cat.categoryId} category={cat} />
          ))}
        </div>
      </div>

      {/* Competitor Benchmarking Matrix */}
      {result.competitorComparison && (
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Competitive Intelligence
              </span>
              <h3 className="text-xl font-bold text-slate-900">
                Head-to-Head Benchmark: @{result.handle} vs. @{result.competitorComparison.competitorHandle}
              </h3>
            </div>
            <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-800 text-xs font-bold uppercase">
              Overall Advantage: {result.competitorComparison.overallAdvantage}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                  <th className="py-2.5 pr-4">Metric</th>
                  <th className="py-2.5 px-4">@{result.handle}</th>
                  <th className="py-2.5 px-4">@{result.competitorComparison.competitorHandle}</th>
                  <th className="py-2.5 pl-4">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.competitorComparison.metrics.map((m, i) => (
                  <tr key={i}>
                    <td className="py-3 pr-4 font-semibold text-slate-800">{m.metric}</td>
                    <td className="py-3 px-4 font-mono text-slate-700">{m.brandValue}</td>
                    <td className="py-3 px-4 font-mono text-slate-600">{m.competitorValue}</td>
                    <td
                      className={`py-3 pl-4 font-mono font-bold ${
                        m.winner === 'brand'
                          ? 'text-emerald-600'
                          : m.winner === 'competitor'
                          ? 'text-rose-600'
                          : 'text-slate-500'
                      }`}
                    >
                      {m.delta}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-2">
            <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-200">
              <h4 className="font-bold text-emerald-900 text-sm mb-2">Competitive Advantages</h4>
              <ul className="space-y-1.5 text-xs text-emerald-950">
                {result.competitorComparison.strengths.map((s, i) => (
                  <li key={i}>✓ {s}</li>
                ))}
              </ul>
            </div>
            <div className="p-4 rounded-xl bg-rose-50/70 border border-rose-200">
              <h4 className="font-bold text-rose-900 text-sm mb-2">Competitive Deficits & Gaps</h4>
              <ul className="space-y-1.5 text-xs text-rose-950">
                {result.competitorComparison.weaknesses.map((w, i) => (
                  <li key={i}>⚠ {w}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* White-Label Consultant PDF Generator */}
      <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm">
        <div className="border-b border-slate-100 pb-4 mb-5">
          <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">
            Agency & Consultant Deliverable
          </span>
          <h3 className="text-xl font-bold text-slate-900">
            Export White-Label Client PDF Report
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Customize the report header with your agency name, custom brand logo, and executive advisory notes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div>
            <label className="block text-slate-700 text-xs font-bold uppercase tracking-wider mb-1.5">
              Agency / Consultant Name
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-slate-700 text-xs font-bold uppercase tracking-wider mb-1.5">
              White-Label Agency Logo (PNG/JPG Optional)
            </label>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleLogoUpload}
              className="w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-slate-700 text-xs font-bold uppercase tracking-wider mb-1.5">
            Custom Consultant Advisory Notes (Included on Page 1 of PDF)
          </label>
          <textarea
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            rows={3}
            placeholder="Add bespoke strategic commentary, campaign timelines, or retainer recommendations for your client..."
            className="w-full border border-slate-300 rounded-lg p-3 text-sm"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-xs text-slate-400">{result.consultantMethodologyNote}</p>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-bold text-sm transition shadow-sm disabled:opacity-50"
          >
            {isGeneratingPdf ? 'Generating White-Label PDF...' : 'Download White-Label PDF Report ↓'}
          </button>
        </div>
      </div>
    </div>
  );
};
